/**
 * The terminal flags of a live streaming turn.
 *
 * Declared here rather than imported from `useChatStream` so this stays a
 * dependency-free pure module; `StreamingTurn` satisfies it structurally, so
 * the hook's object can be handed straight in.
 */
export interface StreamTerminality {
  done: boolean
  awaitingApproval: boolean
}

/**
 * A turn has stopped producing tokens. There are THREE terminal states, not
 * two: `done`, the stream `error` (tracked separately, as `hasError`), and
 * `awaitingApproval` — halted, waiting on a human decision.
 *
 * The halted one is the trap. It is neither a success nor a failure, so any
 * predicate written as "not done" reads it as "still streaming" and spins the
 * typing indicator (and disables the composer) for as long as the user takes to
 * read the approval card that is waiting on THEM.
 *
 * Kept as one exported function rather than a `streamingDone` /
 * `streamingAwaitingApproval` pair on the state, because nothing distinguishes
 * them: every consumer here and in `ChatInner` asks only "has this turn
 * stopped". A fourth terminal flag added later has one place to be taught, and
 * `ChatInner` needs the same answer to decide whether the live bubble may be
 * replaced by the persisted message — hence the export.
 */
export function isStreamTerminal(streaming: StreamTerminality | null | undefined): boolean {
  return !!streaming && (streaming.done || streaming.awaitingApproval)
}

export interface ChatTurnState {
  /** The send POST is in flight. There is only one send now (contract §2). */
  mutationPending: boolean
  /**
   * The user has sent a message this turn and the assistant reply has not yet
   * landed. Bridges the gap between the send POST resolving and the first
   * streamed Centrifugo event arriving, where neither `mutationPending` nor a
   * live turn is present and the loading indicator would otherwise vanish.
   */
  awaitingResponse: boolean
  /**
   * The live turn `ChatInner` is willing to show, or null.
   *
   * Null does NOT mean "the hook has no turn": on mount the hook replays the
   * last completed turn out of backfill, and `ChatInner` withholds a turn that
   * predates the message the user just sent. This field is the turn on SCREEN.
   */
  streaming: StreamTerminality | null
  /** The turn has fully resolved into the persisted message history. */
  turnResolved: boolean
  /** The send failed or the stream reported an error. */
  hasError: boolean
}

/**
 * True while we still owe the user a visible "working…" cue for this turn.
 *
 * The two `!` guards inside the streaming clause are what make this terminate.
 * (The order of the three ORed operands is not — this is a side-effect-free
 * disjunction, and an earlier version of this comment claimed otherwise.)
 *
 * `!isStreamTerminal` retires a turn that finished or halted; `!s.hasError`
 * retires one the stream reported an `error` on. That second guard is not
 * belt-and-braces: `useChatStream` sets `error` WITHOUT touching `done` or
 * `awaitingApproval` and without nulling `streaming`, so an errored turn is
 * indefinitely "started and not finished". Backfill replays from `since: 0`
 * over a ring of 500 events with no TTL and no type filter, so a chat whose
 * last turn errored replays that error on every single mount — without the
 * guard the composer is dead on arrival, for ever, with no in-app recovery.
 */
export function isTurnPending(s: ChatTurnState): boolean {
  return (
    s.mutationPending ||
    s.awaitingResponse ||
    (!s.hasError &&
      s.streaming !== null &&
      !isStreamTerminal(s.streaming) &&
      !s.turnResolved)
  )
}

/** The two fields the anchor test needs. `ChatMessage` satisfies it. */
export interface TurnMessage {
  id: string
  role: string
}

/**
 * Has an assistant message arrived AFTER `anchorId`?
 *
 * This replaces a message-COUNT comparison, which conflated two different
 * reasons for history to grow. If the user sent before `useChatMessages`
 * resolved, the count was captured as 0, and the moment real history landed it
 * read as "the assistant has answered" — clearing the loading cue for a turn
 * that had not started. A count also cannot tell our answer from a colleague's
 * message in the same chat, nor tell the resumed half of an approval turn from
 * the halted half.
 *
 * Two anchor states, and each unfound case resolves in its own safe direction:
 *
 * - `null` — nothing anchored in this mount, so the question degrades to "does
 *   this chat already contain an answer". True is the SAFE reading here: it is
 *   what makes a turn replayed out of backfill count as already resolved, so
 *   the last exchange is not re-rendered as a live bubble on top of its own
 *   persisted copy.
 * - set but absent from the list — the refetch has not landed, so nothing is
 *   proven. False is the safe reading: keep the cue up and the live bubble on
 *   screen rather than blink either out early.
 */
export function hasAssistantAfter(messages: TurnMessage[], anchorId: string | null): boolean {
  return findAssistantAfter(messages, anchorId) !== null
}

/**
 * The assistant message that answered the turn anchored at `anchorId`, or null.
 *
 * The LAST one, not the first: an approval turn persists two assistant messages
 * — the halted one carrying the `approval_request` block, then the resumed
 * answer — and a turn's trace summarises the whole turn, so it belongs with the
 * final message rather than with the pause in the middle.
 *
 * Anchor semantics are exactly those documented on `hasAssistantAfter`, which is
 * now defined in terms of this. It exists separately because pairing a trace to
 * a message needs the message itself: no stream event carries a `message_id`, so
 * this walk is the only join available.
 */
export function findAssistantAfter(
  messages: TurnMessage[],
  anchorId: string | null,
): TurnMessage | null {
  let start = 0
  if (anchorId !== null) {
    const index = messages.findIndex((message) => message.id === anchorId)
    if (index === -1) return null
    start = index + 1
  }
  for (let i = messages.length - 1; i >= start; i -= 1) {
    if (messages[i].role === 'assistant') return messages[i]
  }
  return null
}

/**
 * Show the standalone typing dots only when the turn is pending and no live
 * streaming bubble is on screen to own its own indicator (and not on error).
 */
export function showStandaloneTyping(s: ChatTurnState): boolean {
  const showLiveTurn = s.streaming !== null && !s.turnResolved
  return isTurnPending(s) && !showLiveTurn && !s.hasError
}
